import React, { useEffect, useState, useRef } from 'react';
import { MinusIcon, PlusIcon, ShoppingBagIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useCart } from './CartContext';
const FREE_SHIPPING_THRESHOLD = 200000;
export function CartDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const {
    items,
    itemCount,
    subtotal,
    updateQuantity,
    removeItem,
    isCheckingOut,
    checkoutError,
    clearCheckoutError,
    checkout
  } = useCart();
  const hasFreeShipping = subtotal >= FREE_SHIPPING_THRESHOLD;
  const shippingRemaining = Math.max(FREE_SHIPPING_THRESHOLD - subtotal, 0);
  const shippingProgress = Math.min(subtotal / FREE_SHIPPING_THRESHOLD * 100, 100);
  const closeDrawer = () => {
    setIsOpen(false);
    clearCheckoutError();
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };
  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDrawer();
      if (event.key !== 'Tab' || !drawerRef.current) return;
      const focusable = drawerRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled])');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);
  return <>
      <button ref={triggerRef} type="button" className="cart-trigger" onClick={() => setIsOpen(true)} aria-label={`Abrir carrito${itemCount ? `, ${itemCount} productos` : ''}`} aria-haspopup="dialog" aria-expanded={isOpen} data-id="element-23" data-mp="11hu3b64tjug0">
        <ShoppingBagIcon size={19} strokeWidth={1.45} aria-hidden="true" data-id="element-24" data-mp="1fhv4ynswr5wj" />
        {itemCount > 0 && <span className="cart-trigger__count" data-id="element-25" data-mp="1kgolkp3hrd0x">{itemCount}</span>}
      </button>

      {isOpen && <div className="cart-layer" data-id="element-26" data-mp="1p74zeab850nt">
          <button type="button" className="cart-overlay" aria-label="Cerrar carrito" onClick={closeDrawer} data-id="element-27" data-mp="1p86nbfdc9hmy" />
          <aside ref={drawerRef} className="cart-drawer" role="dialog" aria-modal="true" aria-labelledby="cart-title" data-id="element-28" data-mp="1z6ng8hvu86bn">
            <header className="cart-drawer__header" data-id="element-29" data-mp="3nq00yxyq2kih">
              <div data-id="element-30" data-mp="3nqtc0b4prw6f">
                <p className="cart-drawer__eyebrow" data-id="element-31" data-mp="3nn5j9e73fdsr">SAVIA</p>
                <h2 id="cart-title" data-id="element-32" data-mp="3jmbs8byctivn">Tu carrito</h2>
              </div>
              <button ref={closeButtonRef} type="button" className="cart-icon-button" onClick={closeDrawer} aria-label="Cerrar carrito" data-id="element-33" data-mp="3eo0is2bz1ady">
                <XIcon size={20} strokeWidth={1.5} aria-hidden="true" data-id="element-34" data-mp="b7k1l7pp6ygd" />
              </button>
            </header>

            <div className="cart-drawer__body" data-id="element-35" data-mp="6b3b46v10804">
              {items.length === 0 ? <div className="cart-empty" data-id="element-36" data-mp="1gflg4oad7jy">
                  <ShoppingBagIcon size={28} strokeWidth={1.2} aria-hidden="true" data-id="element-37" data-mp="3souvuy9yrziy" />
                  <p data-id="element-38" data-mp="u0nv8t2x1baz">Tu carrito está en calma.</p>
                  <span data-id="element-39" data-mp="p7kykc2udlqk">Agrega un producto para comenzar tu ritual.</span>
                </div> : <ul className="cart-items" aria-label="Productos en el carrito" data-id="element-40" data-mp="9os6sc7u9unx">
                  {items.map(item => <li className="cart-item" key={item.id} data-id="element-41" data-mp="dsrj0im367bh">
                      {item.image ? <img src={item.image} alt="" className="cart-item__image" data-id="element-42" data-mp="3vyw50ofr201j" /> : <div className="cart-item__image cart-item__image--placeholder" aria-hidden="true" data-id="element-43" data-mp="sioyma5y9op2" />}
                      <div className="cart-item__details" data-id="element-44" data-mp="npmf6uuvzvml">
                        <div className="cart-item__topline" data-id="element-45" data-mp="no1iayl3o0lk">
                          <h3 data-id="element-46" data-mp="1beosjqihn2ba">{item.name}</h3>
                          <button type="button" className="cart-remove" onClick={() => removeItem(item.id)} aria-label={`Eliminar ${item.name} del carrito`} data-id="element-47" data-mp="1bdvtzctonj48">
                            <Trash2Icon size={15} strokeWidth={1.45} aria-hidden="true" data-id="element-48" data-mp="2vwzchlbt4we1" />
                          </button>
                        </div>
                        <p data-id="element-49" data-mp="2hyhqpt5gptyr">{item.aroma}</p>
                        <span data-id="element-50" data-mp="2hzkl4b1zuejf">{item.format}</span>
                        <span data-id="element-51" data-mp="2cz53366roiv4">{formatCop(item.price)} c/u</span>
                        <div className="cart-item__footer" data-id="element-52" data-mp="2qy4wss94zny5">
                          <div className="cart-quantity" aria-label={`Cantidad de ${item.name}`} data-id="element-53" data-mp="2qzppecbw4wtd">
                            <button type="button" onClick={() => updateQuantity(item.id, item.quantity - 1)} aria-label={`Restar una unidad de ${item.name}`} data-id="element-54" data-mp="66vx0bbu12xc">
                              <MinusIcon size={13} strokeWidth={1.5} aria-hidden="true" data-id="element-55" data-mp="301q8kpkexcag" />
                            </button>
                            <span aria-live="polite" data-id="element-56" data-mp="2cbcdeq6j6xbn">{item.quantity}</span>
                            <button type="button" onClick={() => updateQuantity(item.id, item.quantity + 1)} aria-label={`Sumar una unidad de ${item.name}`} data-id="element-57" data-mp="2gbeht73ni4um">
                              <PlusIcon size={13} strokeWidth={1.5} aria-hidden="true" data-id="element-58" data-mp="1slsjsmz0lvo7" />
                            </button>
                          </div>
                          <strong data-id="element-59" data-mp="1l3uekunv96qs">
                            {formatCop(item.price * item.quantity)}
                          </strong>
                        </div>
                      </div>
                    </li>)}
                </ul>}
            </div>

            <footer className="cart-drawer__footer" data-id="element-60" data-mp="173bb80uciouy">
              <div className="cart-shipping-progress" aria-live="polite" data-id="element-61" data-mp="174uqbo49mtv5">
                <p className={`cart-shipping-progress__message${hasFreeShipping ? ' cart-shipping-progress__message--complete' : ''}`} data-id="element-62" data-mp="12aqeaqg57vn7">
                  {hasFreeShipping ? '¡Tienes envío gratis!' : `Te faltan ${formatCop(shippingRemaining)} para envío gratis`}
                </p>
                <div className="cart-shipping-progress__track" role="progressbar" aria-label="Progreso para envío gratis" aria-valuemin={0} aria-valuemax={FREE_SHIPPING_THRESHOLD} aria-valuenow={Math.min(subtotal, FREE_SHIPPING_THRESHOLD)} data-id="element-63" data-mp="jcxg21ggs9w5">
                  <div className="cart-shipping-progress__fill" style={{
                width: `${shippingProgress}%`
              }} data-id="element-64" data-mp="1ybt7ow4hahw6" />
                </div>
              </div>

              <div className="cart-drawer__summary" data-id="element-65" data-mp="1tbf9s6wlcobv">
                {checkoutError && <p className="cart-checkout-error" role="alert" data-id="element-66" data-mp="1em95i4iwc7x4">
                    {checkoutError}
                  </p>}
                <div className="cart-subtotal" data-id="element-67" data-mp="1jihppwbyarb5">
                  <span data-id="element-68" data-mp="15kjbcqxmquvn">Subtotal</span>
                  <strong data-id="element-69" data-mp="15lkznx3npdvb">{formatCop(subtotal)}</strong>
                </div>
                <p className="cart-shipping-note" data-id="element-70" data-mp="1aikufbv22u74">
                  Envío calculado en el checkout.
                </p>
                <button type="button" className="cart-checkout" onClick={checkout} disabled={!items.length || isCheckingOut} data-id="element-71" data-mp="qyckkl788oe2">
                  {isCheckingOut ? 'Preparando checkout…' : 'Finalizar compra'}
                </button>
              </div>
            </footer>
          </aside>
        </div>}
    </>;
}
function formatCop(value: number) {
  return `$${new Intl.NumberFormat('es-CO').format(value)} COP`;
}